/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import './style/notificationInput.scss';

import React, {MouseEventHandler} from 'react';
import {useTranslation} from 'react-i18next';

import {ChangeFunction, NumberString} from '@dvrd/dvr-controls';

import {BluemateButton, BluemateDialog, BluemateInput, BluemateNumberInput} from '../../../controls';

interface Props<T> {
    onActivate: MouseEventHandler;
    onClose: VoidFunction;
    onChange: ChangeFunction<T>;
    onSubmit: VoidFunction;
    value: T;
    parentValue: T;
    label: string;
    active: boolean;
    disabled?: boolean;
    inputProps?: Record<string, any>;
}

export default function NotificationInput<T extends number | string | NumberString>(props: Props<T>) {
    const {t} = useTranslation();
    const {onActivate, onClose, onChange, onSubmit, value, label, active, inputProps, disabled, parentValue} = props;

    function renderInput() {
        if (typeof value === 'string')
            return (
                <BluemateInput
                    onChange={onChange as ChangeFunction<string>}
                    value={value}
                    onEnter={onSubmit}
                    autoFocus
                    shape='rounded'
                    {...inputProps}
                />
            );
        return (
            <BluemateNumberInput
                onChange={onChange as ChangeFunction<NumberString>}
                value={value}
                onEnter={onSubmit}
                autoFocus
                shape='rounded'
                {...inputProps}
            />
        );
    }

    return (
        <>
            <BluemateButton
                outlined
                size='normal'
                label={parentValue instanceof NumberString ? parentValue.stringValue : parentValue}
                onClick={onActivate}
                disabled={disabled}
                className='products-button'
            />
            <BluemateDialog active={active} onClose={onClose} title={label} className='notification-input' hideCancel>
                {renderInput()}
                {/* Own footer instead of the dialog's default, so cancel can
                    sit on the left edge and confirm on the right. Square
                    shapes (the default) throughout — tertiary cancel +
                    primary confirm. */}
                <div className='notification-input-footer'>
                    <BluemateButton
                        tertiary
                        label={t('notifications.components.cancel', 'Annuleren')}
                        onClick={onClose}
                    />
                    <BluemateButton label={t('notifications.components.confirm', 'Bevestigen')} onClick={onSubmit} />
                </div>
            </BluemateDialog>
        </>
    );
}
